import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_HIDDEN_ITEMS, SIDEBAR_ITEMS, SidebarItemId } from '../config/sidebar-items';

type SidebarPreferencesCookieValue = Partial<Record<SidebarItemId, boolean>>;

export const SIDEBAR_PREFERENCES_COOKIE_NAME = 'bonusly_sidebar_prefs';

const SIDEBAR_PREFERENCES_COOKIE_OPTIONS = { expires: 365 };
const VALID_SIDEBAR_ITEM_IDS = new Set(SIDEBAR_ITEMS.map((item) => item.id));

const parseHiddenItems = (cookieValue: string | undefined): Set<SidebarItemId> => {
	const hiddenItems = new Set(DEFAULT_HIDDEN_ITEMS);

	if (!cookieValue) {
		return hiddenItems;
	}

	try {
		const parsedValue = JSON.parse(cookieValue) as unknown;

		if (typeof parsedValue !== 'object' || parsedValue == null || Array.isArray(parsedValue)) {
			return hiddenItems;
		}

		for (const [rawItemId, isVisible] of Object.entries(parsedValue as Record<string, unknown>)) {
			const itemId = rawItemId as SidebarItemId;

			if (VALID_SIDEBAR_ITEM_IDS.has(itemId) && isVisible === false) {
				hiddenItems.add(itemId);
			}
		}
	} catch {
		return hiddenItems;
	}

	return hiddenItems;
};

const writeHiddenItemsCookie = (hiddenItems: ReadonlySet<SidebarItemId>) => {
	const explicitlyHiddenItems = Array.from(hiddenItems).filter((itemId) => !DEFAULT_HIDDEN_ITEMS.has(itemId));

	if (explicitlyHiddenItems.length === 0) {
		Cookies.remove(SIDEBAR_PREFERENCES_COOKIE_NAME);
		return;
	}

	const cookieValue: SidebarPreferencesCookieValue = {};
	for (const itemId of explicitlyHiddenItems) {
		cookieValue[itemId] = false;
	}

	Cookies.set(SIDEBAR_PREFERENCES_COOKIE_NAME, JSON.stringify(cookieValue), SIDEBAR_PREFERENCES_COOKIE_OPTIONS);
};

export const useSidebarPreferences = () => {
	const [hiddenItems, setHiddenItems] = useState<Set<SidebarItemId>>(() => parseHiddenItems(Cookies.get(SIDEBAR_PREFERENCES_COOKIE_NAME)));
	const hasInitialized = useRef(false);

	const isItemVisible = useCallback(
		(itemId: SidebarItemId) => {
			return !hiddenItems.has(itemId);
		},
		[hiddenItems]
	);

	const toggleItem = useCallback((itemId: SidebarItemId) => {
		setHiddenItems((previousHiddenItems) => {
			const nextHiddenItems = new Set(previousHiddenItems);

			if (nextHiddenItems.has(itemId)) {
				nextHiddenItems.delete(itemId);
			} else {
				nextHiddenItems.add(itemId);
			}

			return nextHiddenItems;
		});
	}, []);

	useEffect(() => {
		if (!hasInitialized.current) {
			hasInitialized.current = true;
			return;
		}

		writeHiddenItemsCookie(hiddenItems);
	}, [hiddenItems]);

	return useMemo(
		() => ({
			hiddenItems: hiddenItems as ReadonlySet<SidebarItemId>,
			isItemVisible,
			toggleItem,
		}),
		[hiddenItems, isItemVisible, toggleItem]
	);
};
